import { useMutation, useQuery } from '@tanstack/react-query'
import { EventsFilterParams, CreateEventParams, UpdateEventParams } from '@/types/event'
import { useEventsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useEvents = (params: EventsFilterParams) => {
  const fetchEvents = useEventsStore(state => state.fetchEvents)

  return useQuery({
    queryKey: ['events', params],
    queryFn: () => fetchEvents(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useEvent = (id: string) => {
  const getEvent = useEventsStore(state => state.getEvent)

  return useQuery({
    queryKey: ['event', id],
    queryFn: () => getEvent(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateEvent = () => {
  const createEvent = useEventsStore(state => state.createEvent)

  return useMutation({
    mutationFn: (params: CreateEventParams) => createEvent(params),
    onSuccess: () => {}
  })
}

export const useUpdateEvent = () => {
  const updateEvent = useEventsStore(state => state.updateEvent)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateEventParams }) => updateEvent(id, params),
    onSuccess: () => {}
  })
}

export const useDeleteEvent = () => {
  const deleteEvent = useEventsStore(state => state.deleteEvent)

  return useMutation({
    mutationFn: (id: string) => deleteEvent(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteEvents = () => {
  const bulkDeleteEvents = useEventsStore(state => state.bulkDeleteEvents)
  const deselectAllEvents = useEventsStore(state => state.deselectAllEvents)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteEvents(ids),
    onSuccess: response => {
      deselectAllEvents()
      handleBulkDeleteSuccess(response)
    }
  })
}
